import {graphql, navigate, StaticQuery} from 'gatsby';
import * as React from 'react';
import {getNodes} from '../../customGraphQlModels/Edges';
import {MenuQueryModel} from './MenuContentModel';
import { BBSvg, Brand, ButtonVariant, FSSvg, MarketingSiteBrandEnum, MarketingSiteNav, MarketingSiteNavInternalProps, MarketingSiteNavItemType, MarketingSiteNavProvider, MarketingSiteNavState, MarketingSiteNavThemeTokens, MarketingSiteSvgIconType, MarketingSiteUrls, MFBSvg, ShopNewSvg, SvgFreshStartLogoDark, SvgMyFoodBagLogo, SvgNewWhiteBargainBoxLogo } from '@mfb/lego';
import { getPromoBannerProps } from '../../util/promoBannerUtil';
import { Discount } from '@mfb/cookbook';
import { getPlatform, getPromoGroupCode } from '../../mixpanel/mixpanel';

const ContentfulMenu: React.FunctionComponent<{ hideDiscountBanner: boolean, discount?: Discount }> = ({ hideDiscountBanner, discount }) => (
  <StaticQuery
    query={graphql`
      query {
        allContentfulBrandGroup {
          edges {
            node {
              ...BrandGroupFragment
            }
          }
        }
        site {
          ...SiteMetadataFragment
        }
      }
    `}
    render={(data: MenuQueryModel) => {
      const brand = data.site.siteMetadata.brand;
      const loginUrl = data.site.siteMetadata.accountUrl;
      const brandMenu = getNodes(data.allContentfulBrandGroup).find(
        e => e.brand === brand
      );
      const promoBannerProps = getPromoBannerProps(discount && discount.code ? discount : undefined);
      const menu = brandMenu && brandMenu.brandMenu;

      const contentProps: MarketingSiteNavInternalProps = {
        firstSection: {
          header: getBrandTitle(brand),
        },
        secondSection:{
          navItems: menu?.menuItem?.map(item => ({
            name: item.title,
            urlPath: item.link || '',
            subNavItems: item.items?.map(subItem => ({name: subItem.title, urlPath: subItem.link || ''} as MarketingSiteNavItemType)) || [],
          } as MarketingSiteNavItemType)) || [],
        },
        thirdSection: {
          header: 'Browse by brands',
          brandNavItems: [
              {
                name: 'My Food Bag',
                shortName: 'My Food Bag',
                brand: MarketingSiteBrandEnum.MFB,
                urlPath: data.site.siteMetadata.mfbUrl ?? MarketingSiteUrls.MFB,
                LogoIcon: MFBSvg,
                activeColour: '#F5FAE9',
              },
              {
                name: 'Fresh Start',
                shortName: 'Fresh Start',
                brand: MarketingSiteBrandEnum.FS,
                urlPath: data.site.siteMetadata.fsUrl ?? MarketingSiteUrls.FS,
                LogoIcon: FSSvg,
                activeColour: '#EAF9F7',
              },
              {
                name: 'Bargain Box',
                shortName: 'Bargain Box',
                brand: MarketingSiteBrandEnum.BB,
                urlPath:  data.site.siteMetadata.bbUrl ?? MarketingSiteUrls.BB,
                LogoIcon: BBSvg,
                activeColour: '#FEF8F0',
              },
              {
                name: 'Shop',
                shortName: 'Shop',
                urlPath: data.site.siteMetadata.shopUrl ?? MarketingSiteUrls.SHOP,
                brand: MarketingSiteBrandEnum.SHOP,
                LogoIcon: ShopNewSvg,
                activeColour: '#F5FAE9',
              },
            ],
          //TODO: Auth in website
          user: {
            handleAuthAsync: async () => {
              return;
            },
          },
          logInCtaUrl: loginUrl,
        }
      }

      const marketingSiteNavState: MarketingSiteNavState = {
          buttonVariant: ButtonVariant.Login,
          activeBrand: getBrandWithShop(brand),
          activeUrl: typeof window !== 'undefined' ? window.location.pathname : '',
          content: contentProps,
          theme: getTheme(brand),
          brandIcon: getBrandLogo(brand),
          promoBannerProps: {
            message: promoBannerProps.message,
            countdownText: promoBannerProps.countdownText,
            shouldDisplay: !hideDiscountBanner && promoBannerProps.shouldDisplay
          },
          platform: getPlatform(brand),
          promoGroupCode: getPromoGroupCode(),
      }

      return (
        <MarketingSiteNavProvider 
          initialValue={marketingSiteNavState}   
          onNavigate={(path: string) => {
            navigate(path);
          }}
          onExternalNavigate={(href: string) => {
            window.location.href = href;
          }}
        >
          <MarketingSiteNav className="mp-no-track" />
        </MarketingSiteNavProvider>
      );
    }}
  />
);

export default ContentfulMenu;

const getBrandWithShop = (brand:Brand): MarketingSiteBrandEnum => {
  switch(brand) {
    case Brand.BB:
      return MarketingSiteBrandEnum.BB;
    case Brand.FS:
      return MarketingSiteBrandEnum.FS;
    default:
      return MarketingSiteBrandEnum.MFB;
  }
}

const getTheme = (brand: Brand): MarketingSiteNavThemeTokens => {
  switch(brand) {
    case Brand.BB:
      return {
        fontRegular: "Mulish",
        fontHeader: "Mulish",
        primary_500: "#f7941d",
        primary_600: "#de7d08",
        secondary_700: "#4d216d",
      } as MarketingSiteNavThemeTokens;
    case Brand.FS:
      return {
        fontRegular: "Mulish",
        fontHeader: "Mulish",
        primary_500: "#0fab98",
        primary_600: "#0b786a",
        secondary_700: "#222b59",
      } as MarketingSiteNavThemeTokens;
    default:
      return {
        fontRegular: "Mulish",
        fontHeader: "Mulish",
        primary_500: "#77a222",
        primary_600: "#537118",
        secondary_700: "#00423a",
      } as MarketingSiteNavThemeTokens;
  }
}

const getBrandLogo = (brand: Brand): MarketingSiteSvgIconType => {
  switch(brand) {
    case Brand.BB:
      return SvgNewWhiteBargainBoxLogo;
    case Brand.FS:
      return SvgFreshStartLogoDark;
    default:
      return SvgMyFoodBagLogo;
  }
}

const getBrandTitle = (brand: Brand): string => {
  switch(brand) {
    case Brand.BB:
      return 'Bargain Box';
    case Brand.FS:
      return 'Fresh Start';
    default:
      return 'My Food Bag';
  }
};

